import { createContext, useEffect, useReducer, ReactElement } from 'react';
import {jwtDecode} from 'jwt-decode';
import { LOGIN, LOGOUT, SETORGANIZATION } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';
import Loader from 'components/Loader';
import axios from 'axios';
import { KeyedObject } from 'types/root';
import { AuthProps, JWTContextType } from 'types/auth';
import { registerService, loginService, logoutService, verifyTwoFactorAuthenticationOtp } from '../services/auth.service';
import { GetOrganizationSetting } from '../services/organization.service';
import { getUserByToken } from '../services/user.service';
import { useLocation, useNavigate } from 'react-router';
import { encryptData, decryptData } from '../utils/helper';
import { useDispatch } from 'react-redux';
import { setLogo, setSettings } from 'store/reducers/organizationSettings';
import {
  APP_ADMIN_DEFAULT_PATH,
  APP_DEFAULT_PATH,
  APP_FACULTY_DEFAULT_PATH,
  APP_STUDENT_DEFAULT_PATH,
  APP_STUDENT_TIMETABLE_DEFAULT_PATH
} from 'config';
import { connectSocket, socket } from 'utils/socket';
import useConfig from 'hooks/useConfig';
import Snackbar from 'utils/Snackbar';

// Constants
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false
};

// Axios Interceptors (unchanged)
if (process.env.REACT_APP_ENV === 'development') {
  axios.interceptors.request.use(
    async function (config) {
      if (config.data) {
        if (config.data instanceof FormData) {
          const encryptedData = encryptData(config.data);
          const formData = new FormData();
          formData.append('encryptedData', encryptedData.encrypted);
          const appendToFormData = (data: any, path = '') => {
            if (data instanceof File) {
              formData.append(path, data);
            } else if (Array.isArray(data)) {
              data.forEach((item, index) => {
                appendToFormData(item, `${path}[${index}]`);
              });
            } else if (typeof data === 'object' && data !== null) {
              for (const key in data) {
                const newPath = path ? `${path}[${key}]` : key;
                appendToFormData(data[key], newPath);
              }
            }
          };
          for (const key in encryptedData) {
            if (key === 'encrypted') continue;
            appendToFormData(encryptedData[key], key);
          }
          config.data = formData;
        } else {
          config.data = { encryptedData: encryptData(config.data) };
        }
      }
      if (config.params) {
        config.params = { encryptedData: encryptData(config.params) };
      }
      return config;
    },
    function (error) {
      console.error('Request Error Interceptor:', error);
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      if (response.data && !(response.data instanceof Blob)) {
        if (response.data.data) {
          response.data.data = response.data.data ? decryptData(response.data.data) : decryptData(response.data);
        } else {
          response.data = response.data.data ? decryptData(response.data.data) : decryptData(response.data);
        }
      }
      return response;
    },
    function (error) {
      console.error('Response Error Interceptor:', error);
      return Promise.reject(error);
    }
  );
}

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = async (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.authToken = `${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.authToken;
  }
};

// JWT Context & Provider
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const dispatches = useDispatch();
  const { onChangePresetColor, onChangeMode } = useConfig();

  // Relevant `init` function (unchanged from previous solution)
  const init = async () => {
    try {
      // Check for serviceToken in query parameters (e.g., ?st=token) or localStorage
      const searchParams = new URLSearchParams(location.search);
      let serviceToken = searchParams.get('st') || localStorage.getItem('serviceToken');

      if (serviceToken) {
        await setSession(serviceToken); // Store token in localStorage
        // Clean up the 'st' query parameter to keep URL clean
        if (searchParams.get('st')) {
          searchParams.delete('st');
          const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
          window.history.replaceState(null, '', newUrl);
        }
      }

      if (serviceToken && verifyToken(serviceToken)) {
        const response = await getUserByToken();
        const user = response.data.data;

        const hasSlug =
        (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1 && (!window.location.host?.startsWith('app.') && !window.location.host?.startsWith('lms.')))

        const orgSlug =
          user?.type === 'superadmin' ? (process.env.REACT_APP_HOSTNAME === 'vidyalayalms.com' ? 'app' : process.env.REACT_APP_HOSTNAME === 'sapphiresolutions.in.net' ? 'lms' : '') : user?.organizationId?.slug ? user?.organizationId?.slug : user?.organizationId?.domainUrl;

        let organizationUrl =
          (orgSlug && !hasSlug) ||
          orgSlug !==
            (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1
              ? window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.[0]
              : orgSlug)
            ? `${orgSlug ? (orgSlug + '.') : ''}${process.env.REACT_APP_HOSTNAME}`
            : `${window.location.host}`;

        // Check if current host matches the expected subdomain
        const currentHost = window.location.host;

        if (currentHost !== organizationUrl) {
          // Redirect to subdomain with serviceToken as query parameter
          window.location.replace(
            `${window.location.protocol}//${organizationUrl}${window.location.pathname}${
              window.location.search ? `${window.location.search}&st=${serviceToken}` : `?st=${serviceToken}`
            }`
          );
          return; // Exit to prevent further execution
        }

        let mySetting: any;
        if (response?.data?.data?.organizationId?._id && user?.type !== 'superadmin') {
          mySetting = await GetOrganizationSetting({ orgId: response.data.data.organizationId._id });
          dispatches(setSettings(mySetting.data.data));
          dispatches(setLogo(mySetting.data.data.logoImage));
        }

        if (user?.userTypeId?.permission.Chats?.View || user?.type === 'superadmin') {
          connectSocket();
        }

        if (user?.type === 'student') {
          onChangePresetColor('theme8');
          onChangeMode('light');
        } else {
          onChangePresetColor('default');
        }

        dispatch({
          type: LOGIN,
          payload:
            user?.type !== 'superadmin'
              ? {
                  isLoggedIn: true,
                  isInitialized: true,
                  user,
                  organization: mySetting?.data?.data,
                  permission: response?.data?.data?.userTypeId
                }
              : {
                  isLoggedIn: true,
                  isInitialized: true,
                  user,
                  permission: response?.data?.data?.userTypeId
                }
        });
      } else {
        dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
      }
    } catch (err: any) {
      Snackbar(err.response?.data?.message, 'error');
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
      if (err?.response?.status === 401) navigate('/login');
    }
  };

  useEffect(() => {
    init();
  }, []);

  const login = async (email: string, password: string, webFcmToken: string) => {
    try {
      const loginDetails = { email, password };
      const response = await loginService(loginDetails);
      const userData = response?.data?.data;

      const serviceToken = response.data.data.loginToken ? response.data.data.loginToken : response.data.loginToken;
      await setSession(serviceToken); // Store token locally before redirect (for current domain)

      if (userData?.twoFactorAuthentication) {
        navigate('/two-factor-authentication', {
          state: {
            email: email,
            userId: userData?._id,
            twoFactorAuthenticationWith: userData?.twoFactorAuthenticationWith
          }
        });
      } else {
        const res = await getUserByToken();
        const user = res?.data?.data;

        const hasSlug =
          (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1 && (!window.location.host?.startsWith('app.') && !window.location.host?.startsWith('lms.')))

        const orgSlug =
          user?.type === 'superadmin' ? (process.env.REACT_APP_HOSTNAME === 'vidyalayalms.com' ? 'app' : process.env.REACT_APP_HOSTNAME === 'sapphiresolutions.in.net' ? 'lms' : '') : user?.organizationId?.slug ? user?.organizationId?.slug : user?.organizationId?.domainUrl;

        let organizationUrl =
          (orgSlug && !hasSlug) ||
          orgSlug !==
            (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1
              ? window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.[0]
              : orgSlug)
            ? `${orgSlug ? (orgSlug + '.') : ''}${process.env.REACT_APP_HOSTNAME}`
            : `${window.location.host}`;

        // Check if current host matches the expected subdomain
        const currentHost = window.location.host;
        if (currentHost !== organizationUrl) {
          setSession(null);

          // Redirect to the subdomain URL with serviceToken
          const pathname =
            user?.type === 'student'
              ? user?.userTypeId?.name === 'Aptitude'
                ? APP_STUDENT_TIMETABLE_DEFAULT_PATH
                : APP_STUDENT_DEFAULT_PATH
              : user?.type === 'faculty'
              ? APP_FACULTY_DEFAULT_PATH
              : user?.type === 'admin'
              ? userData?.isFirstLogin
                ? '/getStarted'
                : APP_ADMIN_DEFAULT_PATH
              : user?.type === 'superadmin'
              ? APP_ADMIN_DEFAULT_PATH
              : APP_DEFAULT_PATH;

          window.location.replace(
            `${window.location.protocol}//${organizationUrl}${pathname}${
              window.location.search ? `${window.location.search}&st=${serviceToken}` : `?st=${serviceToken}`
            }`
          );
          return; // Exit to prevent further execution until redirect completes
        }

        let mySetting: any;
        if (userData?.type !== 'superadmin') {
          mySetting = await GetOrganizationSetting({ orgId: response.data.data.organizationId._id });
          dispatches(setSettings(mySetting.data.data));
          dispatches(setLogo(mySetting.data.data.logoImage));
        }

        if (user?.type === 'student') {
          onChangePresetColor('theme8');
          onChangeMode('light');
        } else {
          onChangePresetColor('default');
        }

        if (user?.userTypeId?.permission.Chats?.View) {
          connectSocket();
        }

        dispatch({
          type: LOGIN,
          payload:
            user?.type !== 'superadmin'
              ? {
                  isLoggedIn: true,
                  isInitialized: true,
                  user,
                  organization: mySetting?.data?.data,
                  permission: response?.data?.data?.userTypeId
                }
              : {
                  isLoggedIn: true,
                  isInitialized: true,
                  user,
                  permission: response?.data?.data?.userTypeId
                }
        });

        navigate(
          user?.type === 'student'
            ? user?.userTypeId?.name === 'Aptitude'
              ? APP_STUDENT_TIMETABLE_DEFAULT_PATH
              : APP_STUDENT_DEFAULT_PATH
            : user?.type === 'faculty'
            ? APP_FACULTY_DEFAULT_PATH
            : user?.type === 'admin'
            ? userData?.isFirstLogin
              ? '/getStarted'
              : APP_ADMIN_DEFAULT_PATH
            : user?.type === 'superadmin'
            ? APP_ADMIN_DEFAULT_PATH
            : APP_DEFAULT_PATH
        );
      }
    } catch (error: any) {
      console.error(error);
      Snackbar(error.response?.data?.message || 'Login failed!', 'error');
    }
  };

  const verifyTwoFactorAuthentication = async (otp: string, userId: string, type?: string) => {
    try {
      const response = await verifyTwoFactorAuthenticationOtp({ userId, otp, ...(type ? { type } : {}) });
      await setSession(response.data.data.loginToken ? response.data.data.loginToken : response.data.loginToken);

      const res = await getUserByToken();
      const user = res?.data?.data;

      const hasSlug =
      (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1 && (!window.location.host?.startsWith('app.') && !window.location.host?.startsWith('lms.')))

      const orgSlug =
          user?.type === 'superadmin' ? (process.env.REACT_APP_HOSTNAME === 'vidyalayalms.com' ? 'app' : process.env.REACT_APP_HOSTNAME === 'sapphiresolutions.in.net' ? 'lms' : '') : user?.organizationId?.slug ? user?.organizationId?.slug : user?.organizationId?.domainUrl;

      let organizationUrl =
        (orgSlug && !hasSlug) ||
        orgSlug !==
          (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1
            ? window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.[0]
            : orgSlug)
          ? `${orgSlug ? (orgSlug + '.') : ''}${process.env.REACT_APP_HOSTNAME}`
          : `${window.location.host}`;

      // Check if current host matches the expected subdomain
      const currentHost = window.location.host;
      if (currentHost !== organizationUrl) {
        const pathname =
          user?.type === 'student'
            ? APP_STUDENT_DEFAULT_PATH
            : user?.type === 'faculty'
            ? APP_FACULTY_DEFAULT_PATH
            : user?.type === 'admin'
            ? user?.isFirstLogin
              ? '/getStarted'
              : APP_ADMIN_DEFAULT_PATH
            : user?.type === 'superadmin'
            ? APP_ADMIN_DEFAULT_PATH
            : APP_DEFAULT_PATH;
        // Redirect to the subdomain URL
        window.location.replace(`${window.location.protocol}//${organizationUrl}${pathname}${window.location.search}`);
        return; // Exit to prevent further execution until redirect completes
      }

      let mySetting: any;
      if (user?.type !== 'superadmin') {
        mySetting = await GetOrganizationSetting({ orgId: response.data.data.organizationId._id });
        dispatches(setSettings(mySetting.data.data));
        dispatches(setLogo(mySetting.data.data.logoImage));
      }

      if (user?.type === 'student') {
        onChangePresetColor('theme8');
        onChangeMode('light');
      } else {
        onChangePresetColor('default');
      }

      if (user?.userTypeId?.permission.Chats?.View) {
        connectSocket();
      }

      dispatch({
        type: LOGIN,
        payload:
          user?.type !== 'superadmin'
            ? {
                isLoggedIn: true,
                isInitialized: true,
                user,
                organization: mySetting?.data?.data,
                permission: response?.data?.data?.userTypeId
              }
            : {
                isLoggedIn: true,
                isInitialized: true,
                user,
                permission: response?.data?.data?.userTypeId
              }
      });

      navigate(
        user?.type === 'student'
          ? APP_STUDENT_DEFAULT_PATH
          : user?.type === 'faculty'
          ? APP_FACULTY_DEFAULT_PATH
          : user?.type === 'admin'
          ? user?.isFirstLogin
            ? '/getStarted'
            : APP_ADMIN_DEFAULT_PATH
          : user?.type === 'superadmin'
          ? APP_ADMIN_DEFAULT_PATH
          : APP_DEFAULT_PATH
      );
      localStorage.removeItem('otpTimer');
    } catch (error: any) {
      Snackbar(error.response.data.message ? error.response.data.message : 'Something went wrong!', 'error');
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    vidyalayaMigration: boolean,
    trainingMode: boolean,
    domainUrl: string,
    slug: string,
    storageType: Array<string>,
    currentPermissions: Object,
    classType: string,
    classrooms: Array<string>,
    subjects: Array<string>,
    divisions?: Array<string>,
    streams?: Array<string>
  ) => {
    const registerReq = {
      email,
      password,
      firstName,
      lastName,
      organizationName: company,
      organizationType: 'private',
      isActive: true,
      domainUrl,
      slug,
      storageType,
      vidyalayaMigration,
      trainingMode,
      modulePermissions: currentPermissions,
      classType,
      classrooms,
      subjects,
      divisions,
      streams
    };
    let userRegister = await registerService(registerReq);
    return userRegister;
  };

  const logout = async (userId?: string) => {
    const logoutReq = { userId: state?.user?._id };
    const response = await logoutService(logoutReq);
    if (response.status === 200) {
      setSession(null);
      localStorage.setItem('tabsettings', '0');
      navigate(`/login`);
      socket?.disconnect();
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
    }
  };

  const tokenRemoved = () => {
    dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
  };

  const setOrganization = async (orgId: string | undefined) => {
    const mySetting = await GetOrganizationSetting({ orgId: orgId });
    dispatch({
      type: SETORGANIZATION,
      payload: {
        isLoggedIn: true,
        organization: mySetting?.data?.data
      }
    });
  };

  const resetPassword = async (email: string) => {};

  const updateProfile = async () => {
    try {
      const serviceToken = localStorage.getItem('serviceToken');
      if (serviceToken && verifyToken(serviceToken)) {
        setSession(serviceToken);
        const response = await getUserByToken();
        const user = response.data.data;

        const hasSlug =
        (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1 && (!window.location.host?.startsWith('app.') && !window.location.host?.startsWith('lms.')))

        const orgSlug =
          user?.type === 'superadmin' ? (process.env.REACT_APP_HOSTNAME === 'vidyalayalms.com' ? 'app' : process.env.REACT_APP_HOSTNAME === 'sapphiresolutions.in.net' ? 'lms' : '') : user?.organizationId?.slug ? user?.organizationId?.slug : user?.organizationId?.domainUrl;

        let organizationUrl =
          (orgSlug && !hasSlug) ||
          orgSlug !==
            (window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.length > 1
              ? window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.[0]
              : orgSlug)
            ? `${orgSlug ? (orgSlug + '.') : ''}${process.env.REACT_APP_HOSTNAME}`
            : `${window.location.host}`;

        // Check if current host matches the expected subdomain
        const currentHost = window.location.host;
        if (currentHost !== organizationUrl) {
          // Redirect to the subdomain URL
          window.location.replace(`${window.location.protocol}//${organizationUrl}${window.location.pathname}${window.location.search}`);
          return;
        }

        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user,
            permission: response?.data?.data?.userTypeId
          }
        });
      } else {
        dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
      }
    } catch (err: any) {
      console.error(err);
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
      if (err?.response?.status === 401) navigate('/login');
    }
  };

  const updateOnboardingStatus = async (data: object) => {
    try {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: { ...state.user, onboardingStatus: data }
        }
      });
    } catch (err: any) {
      console.error(err);
      dispatch({ type: LOGOUT, payload: { isLoggedIn: false } });
      if (err?.response?.status === 401) navigate('/login');
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        init,
        login,
        verifyTwoFactorAuthentication,
        tokenRemoved,
        logout,
        register,
        resetPassword,
        updateProfile,
        updateOnboardingStatus,
        setOrganization
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
